import React, { useEffect } from 'react'
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import MagazineStageSimple from '../../Components/MagazineStageSimple/MagazineStageSimple';
import imgCapa from '../../assets/news-events/hydraulic_hose_nomenclature/capa.png';
import imgImagem1 from '../../assets/news-events/hydraulic_hose_nomenclature/imagem1.png';
import imgImagem2 from '../../assets/news-events/hydraulic_hose_nomenclature/imagem2.png';
import imgImagem3 from '../../assets/news-events/hydraulic_hose_nomenclature/imagem3.png';
import imgImagem4 from '../../assets/news-events/hydraulic_hose_nomenclature/imagem4.png';
import MagazineTextBlock from '../../Components/MagazineTextBlock/MagazineTextBlock';

const HydraulicHoseNomenclature = () => {
    useEffect(() => {
        window.scrollTo(0, 0); // Scroll to top on component mount
    }, []);

    const { t } = useTranslation();
    const ogTitle = t('Assembled hydraulic hoses: understanding the HANSA-FLEX nomenclature');
    const ogDescription = t('Learn how to read the HANSA-FLEX nomenclature for assembled hydraulic hoses: cover and hose type, nominal diameter, length, fittings, angles, and accessories, explained through practical examples.');
    const ogImage = `https://www.hansa-flex.com.br/${imgCapa}`;
    const ogUrl = `https://www.hansa-flex.com.br/hydraulic-hose-nomenclature`;

    return (
        <div className="chh-article">
            <Helmet>
                <meta property="og:title" content={ogTitle} />
                <meta property="og:description" content={ogDescription} />
                <meta property="og:image" content={ogImage} />
                <meta property="og:url" content={ogUrl} />
                <meta property="og:type" content="News" />
            </Helmet>

            <MagazineStageSimple
                imageSrc={imgCapa}
                imagePosition="afterTitle"
                imageAlt="Assembled hydraulic hoses: understanding the HANSA-FLEX nomenclature"
                title={"Assembled hydraulic hoses: understanding the HANSA-FLEX nomenclature"}
                text={[
                    "A sequence like P HD 210 x 2000 AOL AFL08 90 may look like just a technical code. But for those who work in maintenance, engineering, or purchasing, it brings together essential information to correctly identify an assembled hose.",
                    "Understanding the nomenclature of HANSA-FLEX assembled hoses helps you quickly interpret what each code means, and this can make everything easier, from a technical specification to the replacement of a hydraulic assembly.",
                ]}
                readingTimeMinutes={7}
            />

            <MagazineTextBlock
                title="Why understand the nomenclature of assembled hoses?"
                paragraphs={[
                    "An assembled hydraulic hose is not just a hose with two fittings. Each assembly must meet the characteristics of the application, including pressure, nominal diameter, fittings, length, and installation conditions.",
                    "For this reason, HANSA-FLEX uses a nomenclature system that brings this information together in a logical sequence.",
                ]}
            />

            <MagazineTextBlock
                imageSrc={imgImagem1}
                imagePosition="beforeTitle"
                imageAlt="In practice, identification follows this order"
                paragraphs={[
                    "In practice, identification follows this order:",
                ]}
                bulletPoints={[
                    "cover and hose type;",
                    "nominal diameter (DN);",
                    "effective length of the assembly in millimeters;",
                    "fitting on side 1;",
                    "fitting on side 2, when different;",
                    "angle or offset of the curved fittings;",
                    "additional accessories, such as abrasion protection.",
                ]}
                paragraphs2={[
                    "If both sides use the same fitting, the designation appears only once. This makes the code more compact without losing the information needed to identify the assembly.",
                ]}
            />

            <MagazineTextBlock
                title="Example 1: P HD 206 x 1000 HS"
                paragraphs={[
                    "Let's start with a simpler configuration:",
                    "<0>P HD 206 x 1000 HS</0>",
                    "<0>P HD</0> indicates a P crimped cover for a high-pressure hose.",
                    "<0>206</0> identifies a high-pressure hose with two braids and nominal diameter DN 6.",
                    "<0>1000</0> represents the effective length of the assembled hose, in millimeters, measured between the sealing cones of the fittings.",
                    "Finally, <0>HS</0> identifies the fitting. Since both sides receive the same model, it appears only once. In this case, it is a connection with an M18 x 1.5 male thread of the heavy series.",
                    "A relatively short sequence, therefore, already describes much of the assembly's configuration.",
                ]}
            />

            <MagazineTextBlock
                imageSrc={imgImagem2}
                imagePosition="beforeTitle"
                imageAlt="Example 2: when curves and offset come into play"
                title="Example 2: when curves and offset come into play"
                paragraphs={[
                    "Now look at:",
                    "<0>SI 104 x 630 AFL90 180° offset</0>",
                    "<0>SI 104</0> identifies a rubber hose from the SI 100 line, with galvanized steel braid and DN 4.",
                    "<0>630</0> corresponds to the effective length of the assembly.",
                    "<0>AFL90</0> indicates fittings with a swivel nut, sealing cone, and a 90° bend.",
                    "<0>180° offset</0> informs the relative position between the two curved fittings.",
                    "This detail is especially important when the hose needs to follow a specific machine geometry. An inadequate orientation can make installation difficult and create unnecessary stresses in the assembly.",
                ]}
            />

            <MagazineTextBlock
                title="And when there are additional accessories?"
                paragraphs={[
                    "The nomenclature also makes it possible to identify accessories installed on the hose.",
                ]}
            />

            <MagazineTextBlock
                imageSrc={imgImagem3}
                imagePosition="beforeTitle"
                imageAlt="Additional accessories in the nomenclature"
                paragraphs={[
                    "One example is:",
                    "<0>P HD 216 x 620 AJ AJ90 SSK</0>",
                    "In this case, <0>SSK</0> indicates that the assembly has an additional plastic protection against wear caused by friction.",
                    "This is relevant in applications where the hose operates close to structures, moving components, or other contact points.",
                    "HANSA-FLEX offers different protection and safety solutions for hoses, allowing the assembly to be adapted to the real conditions of the application.",
                ]}
            />

            <MagazineTextBlock
                title="More complex configurations also follow the same logic"
                paragraphs={[
                    "Consider:",
                    "<0>P HD 525 x 2000 AOS A VA AOS 90 L 120 A VA</0>",
                    "Although it may seem more complex, the reading still follows the same structure.",
                    "<0>P HD 525</0> identifies the hose family and DN 25. <0>2000</0> represents the length in millimeters.",
                    "<0>AOS</0> identifies a metric fitting with a swivel nut and O-Ring of the heavy series. <0>A</0> indicates an Interlock safety device, while <0>VA</0> identifies the stainless steel version.",
                    "The second fitting adds <0>90 L 120</0>, indicating a 90° bend with a 120 mm neck.",
                    "For super-high-pressure hoses of the HD 400, 500, 600, and 700 lines, the crimped fittings receive the abbreviation <0>PA</0>, which identifies the assembly formed by the fitting and the cover.",
                ]}
            />

            <MagazineTextBlock
                imageSrc={imgImagem4}
                imagePosition="beforeTitle"
                imageAlt="More clarity in specification, fewer doubts in replacement"
                title="More clarity in specification, fewer doubts in replacement"
                links={[
                    "https://www.hansa-flex.com.br/x-code-hose-identification-system",
                    "https://www.hansa-flex.com.br/contact-us/",
                ]}
                paragraphs={[
                    "Knowing the nomenclature of HANSA-FLEX assembled hydraulic hoses makes communication easier between maintenance, engineering, purchasing, and supplier. More than interpreting letters and numbers, it is about understanding how each characteristic of the assembly was specified.",
                    "At HANSA-FLEX units, hoses can be produced according to the application, with cutting, crimping, identification and, when necessary, pressure testing. Each hose assembled by HANSA-FLEX also receives an individual identification through the <1>X-CODE</1>, making its identification and future replacements easier.",
                    "Do you need to specify or replace an assembled hose? <2>Contact the HANSA-FLEX technical team</2> to find the right configuration for your application.",
                ]}
            />

            <div style={{ backgroundColor: '#f8f8f8' }}>
                <br />
                <br />
                <br />
            </div>

        </div>
    )
}

export default HydraulicHoseNomenclature
